// =============================================================================
//  tiemposDeDisparo.js
//  DÓNDE SALTA CADA EJERCICIO, AL DÉCIMO DE SEGUNDO
//
//  Rob, 06-10-2026: «tenemos que empatar al microsegundo exacto dónde se
//  dispararán los ejercicios... en un ejercicio doble me di cuenta que le faltó
//  un poquito más de ver el vídeo para que salga el ejercicio y no lo puedo
//  cuadrar perfecto, y esos errores, aunque esté increíble la interfaz, si
//  tenemos problemas de entendimiento el usuario se va a quejar y rompe toda la
//  ilusión del juego».
//
//  ── LO QUE HABÍA ────────────────────────────────────────────────────────────
//  El momento de cada pregunta NO SE GUARDABA. El servidor lo calculaba al
//  vuelo, igual para todos los ejercicios de un segmento:
//
//        disparo = max(20, entero(inicio_del_segmento) + 5)
//
//  Cinco segundos después de empezar el segmento, y nunca antes del 0:20. No
//  había forma de moverlo: ni un campo, ni una pantalla, ni un número. Lo único
//  que se podía hacer era mover el segmento entero —y eso cambia qué texto le
//  toca a la pregunta—.
//
//  Ahora cada ejercicio lleva su `trigger_seconds` dentro, y el servidor lo
//  respeta cuando está. Esto es la cuenta de todo eso, suelta del dibujo:
//  cuánto vale por defecto, cómo se mueve, y qué está mal colocado.
//
//  ── POR QUÉ DÉCIMAS Y NO MÁS ────────────────────────────────────────────────
//  El reloj de YouTube se consulta cada 150 ms y devuelve un número que se mueve
//  a saltos de ese orden. Prometer milésimas sería enseñar tres decimales que el
//  reproductor no puede cumplir: el ejercicio saltaría igual una décima después.
//  Una décima es la unidad más fina que de verdad se nota y se respeta.
// =============================================================================

/** La misma cuenta que hace el servidor cuando un ejercicio no trae la suya. */
export function disparoPorDefecto(inicioDelSegmento) {
    const inicio = Number(inicioDelSegmento);
    if (!Number.isFinite(inicio) || inicio < 0) return 20;
    return Math.max(20, Math.floor(inicio) + 5);
}

/** Décimas de segundo. Ver la cabecera. */
export const aDecima = (s) => Math.round((Number(s) || 0) * 10) / 10;

/** 0:07.4 · 3:12.0 · 1:04:30.5 */
export function relojFino(segundos) {
    const s = Math.max(0, Number(segundos) || 0);
    const enteros = Math.floor(s);
    const decima = Math.round((s - enteros) * 10);
    const h = Math.floor(enteros / 3600);
    const m = Math.floor((enteros % 3600) / 60);
    const r = enteros % 60;
    const base = h > 0
        ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
        : `${m}:${String(r).padStart(2, '0')}`;
    return `${base}.${decima}`;
}

/**
 * El disparo que tiene un ejercicio ahora mismo: el suyo si lo trae, y si no
 * el que le pondría el servidor.
 */
export function disparoDe(ejercicio, inicioDelSegmento) {
    const propio = ejercicio?.data?.trigger_seconds;
    if (typeof propio === 'number' && Number.isFinite(propio) && propio >= 0) {
        return aDecima(propio);
    }
    return disparoPorDefecto(inicioDelSegmento);
}

/**
 * Todos los ejercicios del vídeo con su momento, listos para pintar la línea.
 *
 * Van ORDENADOS POR MOMENTO, no por segmento: la línea de tiempo es el vídeo, y
 * el alumno los ve en ese orden. Si uno del segmento 3 se adelanta al del 2, lo
 * que hay que ver es eso, no la pestaña de la que salió.
 */
export function puntosDeDisparo(segments) {
    const lista = Array.isArray(segments) ? segments : [];
    const puntos = [];
    lista.forEach((seg, i) => {
        (seg?.exercises || []).forEach((ex, j) => {
            puntos.push({
                segId: seg.id,
                exId: ex.id,
                clave: `${seg.id}:${ex.id}`,
                segmento: i + 1,
                enElSegmento: j + 1,
                tipo: ex.type,
                data: ex.data,
                listo: ex.status === 'ready',
                texto: seg.text || '',
                inicio: Number(seg.startTime) || 0,
                fin: Number(seg.endTime) || 0,
                disparo: disparoDe(ex, seg.startTime),
                propio: typeof ex?.data?.trigger_seconds === 'number',
            });
        });
    });
    puntos.sort((a, b) => a.disparo - b.disparo);
    return puntos.map((p, i) => ({ ...p, orden: i + 1 }));
}

/**
 * Mueve un ejercicio a otro momento. Devuelve unos segmentos NUEVOS.
 *
 * El número se guarda DENTRO del ejercicio (`data.trigger_seconds`) a propósito:
 * así viaja con él al guardar, al reabrir y al llegarle al alumno, sin que haya
 * que acordarse de arrastrarlo por tres sitios.
 */
export function mueveDisparo(segments, segId, exId, segundos, duracion = 0) {
    let s = aDecima(segundos);
    if (!Number.isFinite(s) || s < 0) s = 0;
    //  El tope es la duración del vídeo: un ejercicio más allá del final no
    //  salta nunca, y en pantalla no se distingue de uno que se perdió.
    if (duracion > 0 && s > duracion) s = aDecima(duracion);

    return (segments || []).map(seg => {
        if (seg.id !== segId) return seg;
        return {
            ...seg,
            exercises: seg.exercises.map(ex => {
                if (ex.id !== exId) return ex;
                //  Sin `data` no hay dónde guardarlo. Un ejercicio sin generar
                //  no tiene momento que ajustar: se deja igual.
                if (!ex.data || typeof ex.data !== 'object') return ex;
                return { ...ex, data: { ...ex.data, trigger_seconds: s } };
            }),
        };
    });
}

/**
 * Deja escrito el momento de TODOS los ejercicios que no lo tengan.
 *
 * Se llama al entrar a la pantalla de tiempos. Hasta entonces el número vivía
 * sólo en la cabeza del servidor; a partir de aquí está en el dato, y deja de
 * poder cambiar solo porque alguien mueva un segmento.
 */
export function fijaLosQueFaltan(segments) {
    let tocados = 0;
    const nuevos = (segments || []).map(seg => ({
        ...seg,
        exercises: (seg.exercises || []).map(ex => {
            if (!ex.data || typeof ex.data !== 'object') return ex;
            if (typeof ex.data.trigger_seconds === 'number') return ex;
            tocados += 1;
            return { ...ex, data: { ...ex.data, trigger_seconds: disparoPorDefecto(seg.startTime) } };
        }),
    }));
    return { segments: nuevos, tocados };
}

/**
 * Qué está mal colocado. Devuelve una lista de avisos por punto.
 *
 * Son AVISOS, no prohibiciones. Preguntar por algo que todavía no se ha oído es
 * casi siempre un error, pero dejar que la pregunta caiga un poco después del
 * segmento es justo lo que Rob quería poder hacer —«le faltó un poquito más de
 * ver el vídeo»—. Quien sabe cuál de las dos cosas es, es él.
 */
export function avisosDe(punto, duracion = 0) {
    const avisos = [];
    if (!punto) return avisos;

    if (punto.disparo < punto.inicio) {
        avisos.push({
            tipo: 'antes',
            texto: `Salta en ${relojFino(punto.disparo)}, antes de que empiece su `
                + `fragmento (${relojFino(punto.inicio)}): el alumno aún no ha oído la respuesta.`,
        });
    }
    //  ── MUCHO DESPUES DE SU FRAGMENTO (07-10-2026) ───────────────────────
    //  Que un disparo caiga un poco despues de que acabe su fragmento es
    //  normal y suele ser lo que se quiere: se deja acabar la frase y entonces
    //  se pregunta. Que caiga un minuto despues no: el alumno oyo la respuesta
    //  hace rato y para entonces el video va por otra cosa.
    //
    //  Esto aparecio con los ejercicios pegados de otra IA. El pegado acepta el
    //  `disparo` que venga —solo mira que sea un numero y no sea negativo— asi
    //  que una IA que se inventa un tiempo entraba sin que nada lo dijera. Rob
    //  lo habia pedido con esas palabras: «para que la IA no alucine cosas...
    //  sobre todo los tiempos exactos».
    //
    //  Diez segundos de cortesia: con fragmentos de una linea de YouTube, que
    //  duran ocho o nueve, mas que eso ya es otro trozo del video.
    const CORTESIA = 10;
    if (punto.fin > 0 && punto.disparo > punto.fin + CORTESIA) {
        avisos.push({
            tipo: 'tarde',
            texto: `Salta en ${relojFino(punto.disparo)} y su fragmento acaba en `
                + `${relojFino(punto.fin)}: el alumno oyó la respuesta hace `
                + `${relojFino(punto.disparo - punto.fin).replace('0:', '')} s y el vídeo `
                + `ya va por otra cosa.`,
        });
    }

    if (duracion > 0 && punto.disparo >= duracion) {
        avisos.push({
            tipo: 'fuera',
            texto: `Salta en ${relojFino(punto.disparo)} y el vídeo dura `
                + `${relojFino(duracion)}: no llegaría a salir nunca.`,
        });
    }
    return avisos;
}

/**
 * Los avisos de todo el vídeo, incluidos los que sólo se ven comparando.
 */
export function avisosDelVideo(puntos, duracion = 0) {
    const todos = [];
    (puntos || []).forEach((p, i) => {
        for (const a of avisosDe(p, duracion)) todos.push({ ...a, orden: p.orden, clave: p.clave });

        //  Dos preguntas pegadas pero no iguales: el vídeo arranca, corre medio
        //  segundo y vuelve a parar. Se ve como un tirón, no como dos preguntas.
        //  Si comparten momento exacto NO es un aviso: eso es el encadenado, y
        //  es lo que se quiere cuando las dos van del mismo trozo.
        const anterior = puntos[i - 1];
        if (anterior && p.disparo !== anterior.disparo && p.disparo - anterior.disparo < 1.5) {
            todos.push({
                tipo: 'pegados',
                orden: p.orden,
                clave: p.clave,
                texto: `Sale ${relojFino(p.disparo - anterior.disparo).replace('0:', '')} s después `
                    + `de la ${anterior.orden}: el vídeo arrancaría y pararía casi en el sitio. `
                    + `Si las dos van del mismo trozo, ponlas en el mismo momento y se juegan seguidas.`,
            });
        }
    });
    return todos;
}
